/**
 * What the lesson plan says about a concept: what the student will be able
 * to do, the points it covers, and the mistakes people make with it.
 *
 * Shared by the mind map's concept panel and the Knowledge Archive sidebar.
 * Each block shows only when the plan wrote it; with none, nothing renders.
 */

import { Lightbulb, Target, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { InlineMarkdown } from '@/components/core/markdown/markdown';

export interface ConceptNotesProps {
  learningGoal?: string;
  keyPoints?: string[];
  misconceptions?: string[];
}

function Block({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h4 className='mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground'>
        {icon}
        {title}
      </h4>
      {children}
    </section>
  );
}

function Points({ items }: { items: string[] }) {
  return (
    <ul className='list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground marker:text-border'>
      {items.map(item => (
        <li key={item}>
          <InlineMarkdown>{item}</InlineMarkdown>
        </li>
      ))}
    </ul>
  );
}

export function ConceptNotes({
  learningGoal,
  keyPoints = [],
  misconceptions = [],
}: ConceptNotesProps) {
  if (!learningGoal && keyPoints.length === 0 && misconceptions.length === 0) {
    return null;
  }
  return (
    <div className='space-y-4'>
      {learningGoal && (
        <Block
          icon={<Target className='h-4 w-4 text-primary' aria-hidden />}
          title="You'll be able to"
        >
          <p className='text-sm leading-relaxed text-muted-foreground'>
            <InlineMarkdown>{learningGoal}</InlineMarkdown>
          </p>
        </Block>
      )}
      {keyPoints.length > 0 && (
        <Block
          icon={<Lightbulb className='h-4 w-4 text-primary' aria-hidden />}
          title='Key points'
        >
          <Points items={keyPoints} />
        </Block>
      )}
      {misconceptions.length > 0 && (
        <Block
          icon={<TriangleAlert className='h-4 w-4 text-warning' aria-hidden />}
          title='Watch out for'
        >
          <Points items={misconceptions} />
        </Block>
      )}
    </div>
  );
}
